
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/components/ui/use-toast';
import { Tag, Check, X, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';
import { useDiscountSettings } from '@/hooks/useParameters';
import { getDiscountSelectFields } from '@/utils/discountQueryUtils';
import DiscountBadge from '@/components/DiscountBadge';
import CouponCodeButton from '@/components/CouponCodeButton';
import { BusinessDiscount } from '@/hooks/useBusinessDiscount';

interface DiscountWithBusiness extends BusinessDiscount {
  businesses: {
    name: string;
    contact_email: string;
  };
  previous_discount?: {
    discount_amount: number;
    is_percentage: boolean;
    applies_to_all: boolean;
    type: string;
  } | null;
}

const DiscountManagement = () => {
  const [selectedTab, setSelectedTab] = useState('pending');
  const queryClient = useQueryClient();
  const { shouldDisplayDiscountCode } = useDiscountSettings();

  const { data: allDiscounts, isLoading } = useQuery({
    queryKey: ['admin-discounts', shouldDisplayDiscountCode],
    queryFn: async () => {
      const selectFields = getDiscountSelectFields(shouldDisplayDiscountCode);
      const selectString = `${selectFields}, businesses!inner(name, contact_email, is_active)`;
      
      const { data, error } = await supabase
        .from('business_discounts')
        .select(selectString)
        .eq('businesses.is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // For each discount, fetch the current active discount for comparison
      const discountsWithPrevious = await Promise.all(
        data.map(async (discount) => {
          // Get the currently active discount for this business (if any)
          const { data: activeDiscount } = await supabase
            .from('business_discounts')
            .select('discount_amount, is_percentage, applies_to_all, type')
            .eq('business_id', discount.business_id)
            .eq('status', 'Published')
            .eq('is_active', true)
            .neq('id', discount.id) // Exclude the current discount being reviewed
            .single();

          return {
            ...discount,
            previous_discount: activeDiscount || null,
          };
        })
      );

      return discountsWithPrevious as DiscountWithBusiness[];
    },
  });

  const updateDiscountStatus = useMutation({
    mutationFn: async ({ discountId, status, isActive }: { 
      discountId: string; 
      status: string; 
      isActive?: boolean 
    }) => {
      const updates: any = { status };
      if (isActive !== undefined) {
        updates.is_active = isActive;
      }
      
      const { error } = await supabase
        .from('business_discounts')
        .update(updates)
        .eq('id', discountId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-discounts'] });
      toast({
        title: "Success",
        description: "Discount status updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update discount status",
        variant: "destructive",
      });
      console.error('Error updating discount:', error);
    },
  });

  const handleApprove = (discountId: string) => {
    updateDiscountStatus.mutate({ 
      discountId, 
      status: 'Published', 
      isActive: true 
    });
  };

  const handleReject = (discountId: string) => {
    updateDiscountStatus.mutate({ 
      discountId, 
      status: 'Rejected', 
      isActive: false 
    });
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      'Pending Validation': 'bg-yellow-100 text-yellow-800',
      'Published': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
    };
    
    return (
      <Badge className={variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  const getChangeIndicator = (current: DiscountWithBusiness) => {
    if (!current.previous_discount) {
      return (
        <div className="flex items-center gap-1 text-blue-600">
          <Tag className="h-3 w-3" />
          <span className="text-xs">New</span>
        </div>
      );
    }

    const currentValue = current.discount_amount;
    const previousValue = current.previous_discount.discount_amount;
    
    if (currentValue > previousValue) {
      return (
        <div className="flex items-center gap-1 text-green-600">
          <TrendingUp className="h-3 w-3" />
          <span className="text-xs">Increase</span>
        </div>
      );
    } else if (currentValue < previousValue) {
      return (
        <div className="flex items-center gap-1 text-red-600">
          <TrendingDown className="h-3 w-3" />
          <span className="text-xs">Decrease</span>
        </div>
      );
    }
    
    return (
      <div className="flex items-center gap-1 text-orange-600">
        <ArrowRight className="h-3 w-3" />
        <span className="text-xs">Modified</span>
      </div>
    );
  };

  // Filter discounts based on selected tab
  const getFilteredDiscounts = (status: string) => {
    if (!allDiscounts) return [];
    
    switch (status) {
      case 'pending':
        return allDiscounts.filter(d => d.status === 'Pending Validation');
      case 'published':
        return allDiscounts.filter(d => d.status === 'Published');
      case 'rejected':
        return allDiscounts.filter(d => d.status === 'Rejected');
      default:
        return allDiscounts;
    }
  };

  // Calculate counts for badges
  const pendingCount = allDiscounts?.filter(d => d.status === 'Pending Validation').length || 0;
  const publishedCount = allDiscounts?.filter(d => d.status === 'Published').length || 0;
  const rejectedCount = allDiscounts?.filter(d => d.status === 'Rejected').length || 0;

  const currentDiscounts = getFilteredDiscounts(selectedTab);

  if (isLoading) {
    return <div>Loading discounts...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Discount Management</h1>
          <p className="text-muted-foreground">Review and manage business discounts from validated businesses</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-yellow-100 text-yellow-800">
            {pendingCount} pending review
          </Badge>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Business Discounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList>
              <TabsTrigger value="pending">
                Pending 
                {pendingCount > 0 && (
                  <Badge variant="destructive" className="ml-2">{pendingCount}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="published">Published</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
            </TabsList>
            
            <TabsContent value="pending" className="mt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Previous Value</TableHead>
                    <TableHead>New Discount</TableHead>
                    {shouldDisplayDiscountCode && (
                      <TableHead>Coupon Code</TableHead>
                    )}
                    <TableHead>Change</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentDiscounts.map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{discount.businesses.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {discount.businesses.contact_email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {discount.previous_discount ? (
                          <DiscountBadge
                            amount={discount.previous_discount.discount_amount}
                            isPercentage={discount.previous_discount.is_percentage}
                            appliesToAll={discount.previous_discount.applies_to_all}
                            type={discount.previous_discount.type as 'Paid' | 'Free'}
                          />
                        ) : (
                          <span className="text-muted-foreground text-sm">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DiscountBadge
                          amount={discount.discount_amount}
                          isPercentage={discount.is_percentage}
                          appliesToAll={discount.applies_to_all}
                          type={discount.type as 'Paid' | 'Free'}
                        />
                      </TableCell>
                      {shouldDisplayDiscountCode && (
                        <TableCell>
                          {discount.type === 'Paid' && discount.coupon_code ? (
                            <CouponCodeButton
                              couponCode={discount.coupon_code}
                              variant="badge"
                              size="sm"
                            />
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {discount.type === 'Free' ? 'N/A' : 'Pending'}
                            </span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {getChangeIndicator(discount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{discount.type}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(discount.status)}</TableCell>
                      <TableCell>
                        {new Date(discount.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleApprove(discount.id)}
                            disabled={updateDiscountStatus.isPending}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReject(discount.id)}
                            disabled={updateDiscountStatus.isPending}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {currentDiscounts.length === 0 && (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No pending discounts</h3>
                  <p className="text-muted-foreground">All discounts from validated businesses have been reviewed.</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="published" className="mt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Discount</TableHead>
                    {shouldDisplayDiscountCode && (
                      <TableHead>Coupon Code</TableHead>
                    )}
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentDiscounts.map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{discount.businesses.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {discount.businesses.contact_email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DiscountBadge
                          amount={discount.discount_amount}
                          isPercentage={discount.is_percentage}
                          appliesToAll={discount.applies_to_all}
                          type={discount.type as 'Paid' | 'Free'}
                        />
                      </TableCell>
                      {shouldDisplayDiscountCode && (
                        <TableCell>
                          {discount.type === 'Paid' && discount.coupon_code ? (
                            <CouponCodeButton
                              couponCode={discount.coupon_code}
                              variant="badge"
                              size="sm"
                            />
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {discount.type === 'Free' ? 'N/A' : 'No code'}
                            </span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant="outline">{discount.type}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(discount.status)}</TableCell>
                      <TableCell>
                        {new Date(discount.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {currentDiscounts.length === 0 && (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No published discounts</h3>
                  <p className="text-muted-foreground">No discounts have been published yet.</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="rejected" className="mt-6">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Discount</TableHead>
                    {shouldDisplayDiscountCode && (
                      <TableHead>Coupon Code</TableHead>
                    )}
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentDiscounts.map((discount) => (
                    <TableRow key={discount.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{discount.businesses.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {discount.businesses.contact_email}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DiscountBadge
                          amount={discount.discount_amount}
                          isPercentage={discount.is_percentage}
                          appliesToAll={discount.applies_to_all}
                          type={discount.type as 'Paid' | 'Free'}
                        />
                      </TableCell>
                      {shouldDisplayDiscountCode && (
                        <TableCell>
                          {discount.type === 'Paid' && discount.coupon_code ? (
                            <CouponCodeButton
                              couponCode={discount.coupon_code}
                              variant="badge"
                              size="sm"
                            />
                          ) : (
                            <span className="text-muted-foreground text-sm">
                              {discount.type === 'Free' ? 'N/A' : 'No code'}
                            </span>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <Badge variant="outline">{discount.type}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(discount.status)}</TableCell>
                      <TableCell>
                        {new Date(discount.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleApprove(discount.id)}
                            disabled={updateDiscountStatus.isPending}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {currentDiscounts.length === 0 && (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium">No rejected discounts</h3>
                  <p className="text-muted-foreground">No discounts have been rejected.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default DiscountManagement;
